import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEqual } from 'lodash-es';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import type { ActivityItemIconColor } from './ActivityItem';
import ActivityItem from './ActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { EditorActions } from '../editor/editorSlice';
import { gql } from '../graphql/generated';
import CommentPreview from './comment/CommentPreview';
import { useQuill } from '../editor/QuillContext';
import collectThreadAndAnnotationIds from './utils/collectThreadAndAnnotationIds';
import isInlineTextInsertion from '../editor/utils/isInlineTextInsertion';
import type { DocumentType } from '../graphql/generated/gql';
import { AlphaPostActivityType } from '../graphql/generated/graphql';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import commentIcon from '../icons/non-prefilled/16x16/comment.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import helpIcon from '../icons/non-prefilled/16x16/help.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import replyIcon from '../icons/non-prefilled/16x16/reply.svg';
import unarchiveIcon from '../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verifiedFilled.svg';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';
import { isCommentOrPublicCommentThread } from '../utils/post/threadUtils';

const MAX_COMMENT_LINE_COUNT = 3;

// TODO: Before releasing the feature, we will turn this query into a fragment and include it in the PostView query
const CONTROL_BAR_ACTIVITY_QUERY = gql(/** @complexity 25 */ `
  query ControlBarActivity($postId: ID!, $beforeAt: Datetime!) {
    alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
      actedAt
      action
      actors {
        id
        ...ActivityItem_Actors
      }
    }
    post(id: $postId) {
      id
      threads {
        id
        ... on CommentThread {
          id
          comments {
            id
            insertedAt
            content
            author {
              id
              ...ActivityItem_Actors
            }
          }
        }
      }
    }
  }
`);

interface ControlBarActivityProps {
  postId: string;
  closePopover: () => void;
  fullHeight?: boolean;
}

const ControlBarActivity = ({
  postId,
  closePopover,
  fullHeight,
}: ControlBarActivityProps) => {
  const beforeAt = useMemo(
    () => new Date(Date.now() + 1000 * 60 * 5).toISOString(),
    [],
  );

  const { data, loading } = useQuery(CONTROL_BAR_ACTIVITY_QUERY, {
    variables: {
      postId,
      beforeAt,
    },
  });

  if (loading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      <UnresolvedActivitiesSection
        threads={data?.post?.threads}
        onSelectThread={closePopover}
      />
      <h3 className={styles.title}>All activities</h3>
      <div>
        {data?.alphaPostActivities.map((activity) => (
          <ActivityItem
            key={`${activity.actedAt}-${activity.action}`}
            timestamp={activity.actedAt}
            actors={activity.actors}
            {...getDescriptionAndIcon(activity)}
          />
        ))}
      </div>
    </div>
  );
};

const getDescriptionAndIcon = (
  activity: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'][number],
) => {
  const result = (
    description: React.ReactNode,
    icon: string,
    iconColor?: ActivityItemIconColor,
  ) => ({ icon, description, iconColor });

  switch (activity.action) {
    case AlphaPostActivityType.Edit:
      return result('Edited this post', editIcon, 'pink');
    case AlphaPostActivityType.Create:
      return result('Created this post', draftsIcon);
    case AlphaPostActivityType.Verify:
      return result('Verified this post', verifiedIcon, 'green');
    case AlphaPostActivityType.Publish:
      return result('Published this post', publishIcon, 'pink');
    case AlphaPostActivityType.Unpublish:
      return result('Unpublished this post', unpublishIcon);
    case AlphaPostActivityType.Archive:
      return result('Archived this post', archiveIcon, 'pink');
    case AlphaPostActivityType.Unarchive:
      return result('Unarchived this post', unarchiveIcon);
    case AlphaPostActivityType.TransferOwner:
    case AlphaPostActivityType.VerifyExpired:
      return result(activity.action, helpIcon);
    default:
      exhaustiveMatchGuard(activity.action);
      throw new Error(`Unknown activity type: ${activity.action as string}`);
  }
};

interface UnresolvedActivitiesSectionProps {
  threads?: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['threads'];
  onSelectThread: () => void;
}

const UnresolvedActivitiesSection = ({
  threads,
  onSelectThread,
}: UnresolvedActivitiesSectionProps) => {
  const { quill } = useQuill();
  const dispatch = useAppDispatch();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());

  useLayoutEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  const visibleCommentThreads = threads
    ?.map((thread) => {
      const lastComment =
        isCommentOrPublicCommentThread(thread) &&
        postThreadsIds.has(thread.id) &&
        thread.comments.at(-1);

      if (!lastComment) {
        return null;
      }

      return {
        id: thread.id,
        lastComment,
        hasReplies: thread.comments.length > 1,
        timestamp: new Date(lastComment.insertedAt).getTime(),
      };
    })
    .filter((item) => item != null)
    .toSorted((a, b) => b.timestamp - a.timestamp);

  if (!visibleCommentThreads?.length) {
    return null;
  }

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    onSelectThread();
  };

  return (
    <>
      <h3 className={styles.title}>Unresolved</h3>
      <div>
        {visibleCommentThreads.map((thread) => {
          return (
            <ActivityItem
              key={thread.id}
              icon={thread.hasReplies ? replyIcon : commentIcon}
              iconColor="yellow"
              description={
                <CommentPreview
                  collapseLines
                  content={thread.lastComment.content}
                  link={false}
                  maxLines={MAX_COMMENT_LINE_COUNT}
                />
              }
              timestamp={thread.timestamp}
              actors={[thread.lastComment.author]}
              onClick={() => focusCommentThread(thread.id)}
              onMouseEnter={() =>
                dispatch(EditorActions.highlightComment(thread.id))
              }
              onMouseLeave={() =>
                dispatch(EditorActions.removeMagneticWallHighlight())
              }
            />
          );
        })}
      </div>
    </>
  );
};

export default ControlBarActivity;
